/**
 * AshrafieCoinChip
 *
 * Global-header coin chip that surfaces a user's Ashrafie balance one tap from
 * anywhere. Clicking opens a mini-ledger popover: balance, the charity value it
 * can fund, the three most recent ledger entries, and quick links to the full
 * history and the redeem flow.
 *
 * Self-contained like NotificationBell: it owns its data via useAshrafieAccount()
 * and only fetches the mini-ledger when the popover opens (lazy-on-open). Auth
 * gating is handled by the parent (rendered only for authenticated users), and
 * it fails quiet so a balance error never breaks header navigation.
 *
 * Visual language: a minted gold coin token. Antique-gold materials, a reeded
 * coin glyph, an engraved (guilloché) popover header, calm shimmer + hover sheen.
 *
 * Exports:
 *   - AshrafieCoinChip          : interactive desktop pill + popover
 *   - AshrafieMobileBalanceRow  : static balance row for the mobile menu drawer
 *
 * @module components/ashrafie/AshrafieCoinChip
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Heart,
  Lock,
  Settings,
  Sparkles,
  Undo2,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { LocaleLink } from '@lib/i18n/exports';
import PortalDropdown, { usePortalDropdown } from '@shared/ui/PortalDropdown';
import {
  ASHRAFIE_MIN_REDEEM_POINTS,
  extractTxnPoints,
  formatPoints,
  formatRelativeTime,
  pointsToGiveValue,
  useAshrafieAccount,
  useAshrafieHistory,
  type AshrafieTransaction,
  type AshrafieTransactionType,
} from '@lib/api/ashrafie';
import { AshrafieCoinGlyph } from './AshrafieCoinGlyph';

// ─────────────────────────────────────────────────────────────────────────────
// Per-type presentation for the compact recent rows
// ─────────────────────────────────────────────────────────────────────────────

const TXN_STYLE: Record<
  AshrafieTransactionType,
  {
    Icon: React.FC<{ className?: string }>;
    bg: string;
    fg: string;
    accent: string;
    sign: '+' | '-' | '';
  }
> = {
  EARN: {
    Icon: ArrowDownLeft,
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    fg: 'text-emerald-600 dark:text-emerald-400',
    accent: 'bg-emerald-400',
    sign: '+',
  },
  REDEEM: {
    Icon: Heart,
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    fg: 'text-rose-600 dark:text-rose-400',
    accent: 'bg-rose-400',
    sign: '-',
  },
  ADJUSTMENT: {
    Icon: Settings,
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    fg: 'text-amber-600 dark:text-amber-400',
    accent: 'bg-amber-400',
    sign: '',
  },
  REVERSAL: {
    Icon: Undo2,
    bg: 'bg-gray-100 dark:bg-gray-800',
    fg: 'text-gray-500 dark:text-gray-400',
    accent: 'bg-gray-300 dark:bg-gray-600',
    sign: '',
  },
};

/** Reusable hover sheen sweep, clipped to the parent's rounded shape. */
const SheenSweep: React.FC = () => (
  <span
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
  >
    <span className="absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-linear-to-r from-transparent via-white/55 to-transparent opacity-0 transition-all duration-700 ease-out group-hover:left-[125%] group-hover:opacity-100" />
  </span>
);

/** Safe digit-string to BigInt for the redeem-eligibility comparison. */
function balanceToBigInt(balance: string | undefined): bigint {
  if (!balance || !/^-?\d+$/.test(balance)) return 0n;
  try {
    return BigInt(balance);
  } catch {
    return 0n;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Desktop chip + popover
// ─────────────────────────────────────────────────────────────────────────────

export const AshrafieCoinChip: React.FC = () => {
  const { t, i18n } = useTranslation('ashrafie');
  const locale = i18n.language;
  const { data, isLoading, isError } = useAshrafieAccount();
  const { isOpen, close, toggle, triggerRef, dropdownProps } =
    usePortalDropdown();

  // Lazy: the mini-ledger only fetches once the popover is open.
  const history = useAshrafieHistory(3, { enabled: isOpen });
  const recent = (history.data?.pages?.[0]?.items ?? []).slice(0, 3);

  const canRedeem =
    !data?.isFrozen &&
    balanceToBigInt(data?.balance) >= ASHRAFIE_MIN_REDEEM_POINTS;

  // Fail quiet: a balance widget must never break the header.
  if (isError && !data) return null;

  // Skeleton with a fixed width to avoid layout shift during first load.
  if (isLoading && !data) {
    return (
      <div
        aria-hidden="true"
        className="h-10 w-[96px] animate-pulse rounded-full bg-[#F1E6CC] dark:bg-gray-800"
      />
    );
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('chip.ariaLabel', {
          balance: formatPoints(data?.balance, { locale }),
        })}
        className={cn(
          'group relative inline-flex h-10 items-center gap-1.5 overflow-hidden rounded-full pl-1.5 pr-2.5',
          'border border-[#E4CF9E] bg-[linear-gradient(135deg,#FBF3DE_0%,#F6E7C2_46%,#FBF4E2_100%)]',
          'shadow-[0_1px_2px_rgba(110,83,32,0.18),inset_0_1px_0_rgba(255,255,255,0.75)]',
          'transition-all duration-200 hover:-translate-y-px',
          'hover:shadow-[0_8px_18px_-6px_rgba(170,130,40,0.5),inset_0_1px_0_rgba(255,255,255,0.85)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B8902F]',
          'dark:border-[#5C4A2C] dark:bg-[linear-gradient(135deg,#2A2418,#1F1B12)]',
          data?.isFrozen && 'opacity-70'
        )}
      >
        <SheenSweep />
        <AshrafieCoinGlyph size={26} shimmer />
        <span className="relative font-display text-sm font-bold tabular-nums tracking-tight text-[#4A3A18] dark:text-[#F2DD93]">
          {formatPoints(data?.balance, { locale, compact: true })}
        </span>
        {data?.isFrozen ? (
          <Lock className="relative h-3.5 w-3.5 text-[#A9842F]" aria-hidden="true" />
        ) : (
          <ChevronDown
            className={cn(
              'relative h-3.5 w-3.5 text-[#A9842F] transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            aria-hidden="true"
          />
        )}
      </button>

      <PortalDropdown
        {...dropdownProps}
        placement="bottom-end"
        minWidth={312}
        maxWidth={344}
        offset={10}
        estimatedHeight={400}
        className="overflow-hidden rounded-2xl border border-[#EADFBF] bg-white shadow-[0_24px_60px_-18px_rgba(110,83,32,0.45)] dark:border-gray-700 dark:bg-gray-900"
      >
        <div role="dialog" aria-label={t('chip.title')} className="w-full">
          {/* Engraved gold header -- echoes a minted coin / the wallet card */}
          <div className="relative overflow-hidden bg-[linear-gradient(135deg,#211C12_0%,#2C2516_52%,#1B1710_100%)] px-5 py-4">
            {/* Guilloché engraving (concentric rings) */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[repeating-radial-gradient(circle_at_82%_-20%,rgba(226,198,103,0.10)_0,rgba(226,198,103,0.10)_1px,transparent_1px,transparent_8px)]"
            />
            {/* Warm corner glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_26%_18%,rgba(242,221,147,0.18)_0%,transparent_55%)]"
            />
            {/* The whole balance block opens the Ashrafie hub -- desktop's
                path to /dashboard/ashrafie, matching the mobile row. */}
            <LocaleLink
              to="/dashboard/ashrafie"
              onClick={close}
              aria-label={t('chip.openHub', 'Open your Ashrafie')}
              className="group/hub relative block rounded-lg transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E2C667]"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[0.66rem] font-semibold uppercase tracking-[0.2em] text-[#E2C667]/80">
                  {t('chip.title')}
                  <ArrowUpRight
                    className="h-3 w-3 opacity-0 transition-all duration-200 group-hover/hub:translate-x-0.5 group-hover/hub:opacity-100"
                    aria-hidden="true"
                  />
                </span>
                <AshrafieCoinGlyph size={24} shimmer />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-[2rem] font-extrabold leading-none tracking-tight tabular-nums text-white">
                  {formatPoints(data?.balance, { locale })}
                </span>
                <span className="text-[0.66rem] font-semibold uppercase tracking-[0.2em] text-[#E2C667]/75">
                  {t('chip.unit')}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-sm font-medium text-[#F0DCA0]">
                {!data?.isFrozen && (
                  <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                )}
                <span>
                  {data?.isFrozen
                    ? t('chip.frozen')
                    : t('chip.readyToGive', {
                        amount: pointsToGiveValue(data?.balance, { locale }),
                      })}
                </span>
              </div>
            </LocaleLink>
          </div>

          {/* Gold gradient hairline */}
          <div
            aria-hidden="true"
            className="h-px bg-[linear-gradient(90deg,transparent,rgba(201,162,75,0.55),transparent)]"
          />

          {/* Recent activity */}
          <div className="px-2 py-2">
            <div className="px-3 pb-1 pt-1.5 text-[0.64rem] font-semibold uppercase tracking-[0.16em] text-[#A9842F]">
              {t('chip.recent')}
            </div>
            {history.isPending ? (
              <RecentSkeleton />
            ) : recent.length === 0 ? (
              <p className="px-3 py-5 text-center text-xs text-gray-400">
                {t('chip.empty')}
              </p>
            ) : (
              <ul className="space-y-0.5">
                {recent.map((txn) => (
                  <RecentRow
                    key={txn.id}
                    txn={txn}
                    label={txn.description ?? t(`chip.txn.${txn.type}`)}
                    locale={locale}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Footer actions */}
          <div className="grid grid-cols-2 gap-2 border-t border-[#F0E7D0] p-3 dark:border-gray-800">
            <LocaleLink
              to="/dashboard/ashrafie/transactions"
              onClick={close}
              className="inline-flex h-9 items-center justify-center rounded-xl border border-[#E4CF9E] bg-white text-xs font-semibold text-[#5A431A] transition-colors hover:bg-[#FBF4E2] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t('chip.allTransactions', 'All transactions')}
            </LocaleLink>
            {canRedeem ? (
              <LocaleLink
                to="/dashboard/ashrafie/redeem"
                onClick={close}
                className="group relative inline-flex h-9 items-center justify-center gap-1 overflow-hidden rounded-xl bg-[linear-gradient(135deg,#C49A3C_0%,#9A7B2E_100%)] text-xs font-bold text-white shadow-[0_2px_8px_-2px_rgba(154,123,46,0.6)] transition-all hover:shadow-[0_8px_18px_-5px_rgba(154,123,46,0.7)]"
              >
                <SheenSweep />
                <span className="relative">{t('chip.redeem')}</span>
                <ArrowRight className="relative h-3.5 w-3.5" aria-hidden="true" />
              </LocaleLink>
            ) : (
              <span
                title={
                  data?.isFrozen
                    ? t('chip.frozen')
                    : t('chip.needMore', {
                        min: formatPoints(String(ASHRAFIE_MIN_REDEEM_POINTS), {
                          locale,
                        }),
                      })
                }
                className="inline-flex h-9 cursor-not-allowed items-center justify-center rounded-xl bg-gray-100 text-xs font-semibold text-gray-400 dark:bg-gray-800 dark:text-gray-600"
              >
                {t('chip.redeem')}
              </span>
            )}
          </div>
        </div>
      </PortalDropdown>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const RecentRow: React.FC<{
  txn: AshrafieTransaction;
  label: string;
  locale: string;
}> = ({ txn, label, locale }) => {
  const style = TXN_STYLE[txn.type];
  const Icon = style.Icon;
  const points = extractTxnPoints(txn.metadata);

  return (
    <li className="relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-[#FBF6EA] dark:hover:bg-gray-800/60">
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full',
          style.accent
        )}
      />
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          style.bg
        )}
      >
        <Icon className={cn('h-4 w-4', style.fg)} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.8rem] font-medium text-gray-800 dark:text-gray-100">
          {label}
        </span>
        <span className="block text-[0.7rem] text-gray-400">
          {formatRelativeTime(txn.createdAt, locale)}
        </span>
      </span>
      {points !== null && style.sign !== '' && (
        <span
          className={cn(
            'shrink-0 font-display text-xs font-bold tabular-nums',
            style.fg
          )}
        >
          {style.sign}
          {formatPoints(points, { locale })}
        </span>
      )}
    </li>
  );
};

const RecentSkeleton: React.FC = () => (
  <ul className="space-y-0.5" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <li key={i} className="flex items-center gap-3 px-3 py-2">
        <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
        <span className="flex-1 space-y-1.5">
          <span className="block h-3 w-2/3 animate-pulse rounded-sm bg-gray-100 dark:bg-gray-800" />
          <span className="block h-2.5 w-1/3 animate-pulse rounded-sm bg-gray-100 dark:bg-gray-800" />
        </span>
      </li>
    ))}
  </ul>
);

// ─────────────────────────────────────────────────────────────────────────────
// Mobile balance row (static, for the slide-out menu)
// ─────────────────────────────────────────────────────────────────────────────

export const AshrafieMobileBalanceRow: React.FC<{ onNavigate?: () => void }> = ({
  onNavigate,
}) => {
  const { t, i18n } = useTranslation('ashrafie');
  const locale = i18n.language;
  const { data, isLoading, isError } = useAshrafieAccount();

  if (isError && !data) return null;

  return (
    <LocaleLink
      to="/dashboard/ashrafie"
      onClick={onNavigate}
      aria-label={t('chip.ariaLabel', {
        balance: formatPoints(data?.balance, { locale }),
      })}
      className="group relative mt-2 flex items-center gap-3 overflow-hidden rounded-2xl border border-[#E4CF9E] bg-[linear-gradient(135deg,#FBF3DE_0%,#F6E7C2_55%,#FBF4E2_100%)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-transform active:scale-[0.99] dark:border-[#5C4A2C] dark:bg-[linear-gradient(135deg,#2A2418,#1F1B12)]"
    >
      <SheenSweep />
      <AshrafieCoinGlyph size={36} shimmer />
      <span className="relative min-w-0 flex-1">
        <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-[#8A6A28] dark:text-[#E2C667]">
          {t('chip.title')}
        </span>
        {isLoading && !data ? (
          <span className="mt-1 block h-4 w-20 animate-pulse rounded-sm bg-[#E7D6AE] dark:bg-gray-700" />
        ) : (
          <span className="font-display text-lg font-bold leading-tight tabular-nums text-[#4A3A18] dark:text-white">
            {formatPoints(data?.balance, { locale })}{' '}
            <span className="text-[0.7rem] font-semibold uppercase text-[#A9842F] dark:text-gray-400">
              {t('chip.unit')}
            </span>
          </span>
        )}
      </span>
      <span className="relative shrink-0 text-right">
        <span className="block text-[0.6rem] font-medium uppercase tracking-[0.12em] text-[#A9842F]/80">
          {t('chip.readyToGiveShort')}
        </span>
        <span className="block font-display text-sm font-bold text-[#8A6A28] dark:text-[#E2C667]">
          {pointsToGiveValue(data?.balance, { locale })}
        </span>
      </span>
    </LocaleLink>
  );
};

export default AshrafieCoinChip;
